import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Typography, Input, Button } from '@material-tailwind/react';
import axios from 'axios';
import { FaTrash, FaPlus, FaCheck, FaTimes, FaPenAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';
import ConfirmModal from '../../common/ConfirmModal';
import Select from 'react-select';
const { rootUrl } = require('../../../config/config');

const analysisCategoryOptions = [
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'SETTLEMENT', label: 'Settlement' },
    { value: 'ADJUSTMENT', label: 'Adjustment' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'OTHER', label: 'Other' },
];

const subAnalysisCategoryOptions = [
    { value: 'OWNER', label: 'Owner' },
    { value: 'VEHICLE', label: 'Vehicle' },
    { value: 'CASHBOOK', label: 'Cashbook' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'JOB_CARD', label: 'Job card' },
    { value: 'SUPPLIER', label: 'Supplier' },
    { value: 'PERIOD', label: 'Period' },
    { value: 'REFERENCE', label: 'Reference' },
    { value: 'OTHER', label: 'Other' },
];

const MasterCodeSection = ({
    title,
    description,
    endpoint,
    categoryOptions,
    createLabel,
    deactivateLabel,
    emptyMessage,
}) => {
    const { user, token } = useAuth();
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingCode, setEditingCode] = useState(null);
    const [newCode, setNewCode] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newCategory, setNewCategory] = useState(null);
    const [newActive, setNewActive] = useState(true);
    const [editDescription, setEditDescription] = useState('');
    const [editCategory, setEditCategory] = useState(null);
    const [editActive, setEditActive] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchCodes = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}${endpoint}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCodes(Array.isArray(response.data?.data) ? response.data.data : []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || `Failed to fetch ${title.toLowerCase()}.`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, token]);

    const startEdit = (code) => {
        setEditingCode(code.code);
        setEditDescription(code.description || '');
        setEditCategory(code.category ? categoryOptions.find((option) => option.value === code.category) : null);
        setEditActive(Boolean(code.is_active));
    };

    const cancelEdit = () => {
        setEditingCode(null);
        setEditDescription('');
        setEditCategory(null);
        setEditActive(false);
    };

    const createCode = async (event) => {
        event.preventDefault();
        if (!newCode.trim() || !newDescription.trim()) {
            setError('Code and description are required.');
            return;
        }

        try {
            await axios.post(`${rootUrl}${endpoint}`, {
                code: newCode.toUpperCase().trim(),
                description: newDescription.trim(),
                category: newCategory?.value || null,
                is_active: newActive,
                input_by: user?.username || 'system',
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setNewCode('');
            setNewDescription('');
            setNewCategory(null);
            setNewActive(true);
            setError('');
            fetchCodes();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to create ${title.toLowerCase()}.`);
        }
    };

    const saveEdit = async (code) => {
        try {
            await axios.put(`${rootUrl}${endpoint}/${code}`, {
                description: editDescription.trim(),
                category: editCategory?.value || null,
                is_active: editActive,
                edit_by: user?.username || 'system',
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            cancelEdit();
            fetchCodes();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to update ${title.toLowerCase()}.`);
        }
    };

    const deleteCode = (code) => {
        setDeleteTarget(code);
        setDeleteError('');
    };

    const confirmDeleteCode = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);

        try {
            await axios.delete(`${rootUrl}${endpoint}/${deleteTarget}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setDeleteTarget(null);
            fetchCodes();
        } catch (err) {
            setDeleteError(err.response?.data?.message || `Failed to deactivate ${title.toLowerCase()}.`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const selectedNewCategory = useMemo(
        () => categoryOptions.find((option) => option.value === newCategory?.value) || null,
        [categoryOptions, newCategory]
    );

    return (
        <div className="space-y-4">
            {error && (
                <div>
                    <Alerty open={Boolean(error)} color="orange" message={error} setOpen={setError} />
                </div>
            )}

            <form onSubmit={createCode} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <div className="flex items-start justify-between gap-4 flex-col lg:flex-row lg:items-center mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">{createLabel}</h3>
                        <p className="text-sm text-gray-500">{description}</p>
                    </div>
                    <Button
                        type="submit"
                        className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-3 shadow-none"
                    >
                        <FaPlus className="inline mr-2" /> Add Code
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Code <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value.toUpperCase().slice(0, 30))}
                            placeholder="Code"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            placeholder="Enter description"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                        </label>
                        <Select
                            value={selectedNewCategory}
                            onChange={setNewCategory}
                            options={categoryOptions}
                            isClearable
                            placeholder="Select category"
                            className="text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={newActive}
                                onChange={(e) => setNewActive(e.target.checked)}
                            />
                            Active
                        </label>
                    </div>
                </div>
            </form>

            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border px-4 py-2 text-left">Code</th>
                                <th className="border px-4 py-2 text-left">Description</th>
                                <th className="border px-4 py-2 text-left">Category</th>
                                <th className="border px-4 py-2 text-center">Status</th>
                                <th className="border px-4 py-2 text-left text-sm">Created By</th>
                                <th className="border px-4 py-2 text-left text-sm">Created Date</th>
                                <th className="border px-4 py-2 text-left text-sm">Edited By</th>
                                <th className="border px-4 py-2 text-left text-sm">Edited Date</th>
                                <th className="border px-4 py-2 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="border px-4 py-6 text-center text-sm text-gray-500">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            ) : codes.map((code) => (
                                <tr key={code.code} className="hover:bg-gray-50">
                                    <td className="border px-4 py-2 font-mono font-bold">{code.code}</td>
                                    <td className="border px-4 py-2">
                                        {editingCode === code.code ? (
                                            <Input
                                                type="text"
                                                size="sm"
                                                value={editDescription}
                                                onChange={(e) => setEditDescription(e.target.value)}
                                            />
                                        ) : (
                                            <Typography>{code.description}</Typography>
                                        )}
                                    </td>
                                    <td className="border px-4 py-2">
                                        {editingCode === code.code ? (
                                            <Select
                                                value={editCategory}
                                                onChange={setEditCategory}
                                                options={categoryOptions}
                                                isClearable
                                                placeholder="Select category"
                                                className="text-sm"
                                            />
                                        ) : (
                                            <Typography className="text-sm">{code.category || '—'}</Typography>
                                        )}
                                    </td>
                                    <td className="border px-4 py-2">
                                        {editingCode === code.code ? (
                                            <label className="inline-flex cursor-pointer mx-auto flex justify-center items-center">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={editActive}
                                                    onChange={(e) => setEditActive(e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:bg-blue-600 peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform" />
                                            </label>
                                        ) : code.is_active ? (
                                            <div className="flex justify-center items-center">
                                                <FaCheck className="text-green-700" />
                                            </div>
                                        ) : (
                                            <div className="flex justify-center items-center">
                                                <FaTimes className="text-red-700" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{code.input_by || '—'}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{formatTimestamp(code.input_date)}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{code.edit_by || '—'}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{formatTimestamp(code.edit_date)}</td>
                                    <td className="border px-4 py-2 text-center space-y-2 sm:space-y-0 sm:space-x-2">
                                        {editingCode === code.code ? (
                                            <>
                                                <Button onClick={() => saveEdit(code.code)} className="bg-transparent shadow-none" title="Save">
                                                    <FaCheck className="text-green-700" />
                                                </Button>
                                                <Button onClick={cancelEdit} className="bg-transparent shadow-none" title="Cancel">
                                                    <FaTimes className="text-orange-700" />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button onClick={() => startEdit(code)} className="bg-transparent shadow-none" title="Edit">
                                                    <FaPenAlt className="text-blue-gray-700" />
                                                </Button>
                                                <Button onClick={() => deleteCode(code.code)} className="bg-transparent shadow-none" title={deactivateLabel}>
                                                    <FaTrash className="text-red-700" />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                title={deactivateLabel}
                description={`Are you sure you want to deactivate ${deleteTarget || 'this code'}?`}
                confirmText="Deactivate"
                cancelText="Cancel"
                variant="danger"
                loading={deleteLoading}
                error={deleteError}
                onCancel={() => {
                    if (deleteLoading) return;
                    setDeleteTarget(null);
                    setDeleteError('');
                }}
                onConfirm={confirmDeleteCode}
            />
        </div>
    );
};

const MappingSection = () => {
    const { user, token } = useAuth();
    const [mappings, setMappings] = useState([]);
    const [analysisCodes, setAnalysisCodes] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newAnalysisCode, setNewAnalysisCode] = useState(null);
    const [newGlAccount, setNewGlAccount] = useState(null);
    const [newActive, setNewActive] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token}` };
            const [mappingRes, analysisRes, accountsRes] = await Promise.all([
                axios.get(`${rootUrl}/api/system/gl-analysis-code-mappings`, { headers }),
                axios.get(`${rootUrl}/api/owner-analysis-codes`, { headers }),
                axios.get(`${rootUrl}/system/coa/accounts`, { headers }),
            ]);

            setMappings(Array.isArray(mappingRes.data?.data) ? mappingRes.data.data : []);
            setAnalysisCodes(Array.isArray(analysisRes.data?.data) ? analysisRes.data.data : []);
            setGlAccounts(Array.isArray(accountsRes.data?.data) ? accountsRes.data.data.filter((account) => account.is_enabled !== false) : []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch GL analysis mappings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const analysisOptions = useMemo(() => (
        analysisCodes.map((code) => ({
            value: code.code,
            label: `${code.code}${code.description ? ` - ${code.description}` : ''}`,
        }))
    ), [analysisCodes]);

    const glAccountOptions = useMemo(() => (
        glAccounts.map((account) => ({
            value: account.account_code,
            label: `${account.account_code} - ${account.account_name}`,
        }))
    ), [glAccounts]);

    const createMapping = async (event) => {
        event.preventDefault();

        if (!newAnalysisCode?.value || !newGlAccount?.value) {
            setError('Analysis code and GL account are required.');
            return;
        }

        try {
            await axios.post(`${rootUrl}/api/system/gl-analysis-code-mappings`, {
                analysis_code: newAnalysisCode.value,
                gl_account_code: newGlAccount.value,
                is_active: newActive,
                input_by: user?.username || 'system',
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setNewAnalysisCode(null);
            setNewGlAccount(null);
            setNewActive(true);
            setError('');
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create GL analysis mapping.');
        }
    };

    const confirmDeleteMapping = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);

        try {
            await axios.delete(
                `${rootUrl}/api/system/gl-analysis-code-mappings/${deleteTarget.analysis_code}/${deleteTarget.gl_account_code}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDeleteTarget(null);
            fetchData();
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Failed to deactivate mapping.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-4">
            {error && <Alerty open={Boolean(error)} color="orange" message={error} setOpen={setError} />}

            <form onSubmit={createMapping} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <div className="flex items-start justify-between gap-4 flex-col lg:flex-row lg:items-center mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Map analysis code to GL account</h3>
                        <p className="text-sm text-gray-500">Control which GL accounts can use each analysis code.</p>
                    </div>
                    <Button type="submit" className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-3 shadow-none">
                        <FaPlus className="inline mr-2" /> Add Mapping
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Analysis code <span className="text-red-500">*</span></label>
                        <Select
                            value={newAnalysisCode}
                            onChange={setNewAnalysisCode}
                            options={analysisOptions}
                            isClearable
                            placeholder="Select analysis code"
                            className="text-sm"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">GL account <span className="text-red-500">*</span></label>
                        <Select
                            value={newGlAccount}
                            onChange={setNewGlAccount}
                            options={glAccountOptions}
                            isClearable
                            placeholder="Select GL account"
                            className="text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={newActive}
                                onChange={(e) => setNewActive(e.target.checked)}
                            />
                            Active
                        </label>
                    </div>
                </div>
            </form>

            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border px-4 py-2 text-left">Analysis Code</th>
                                <th className="border px-4 py-2 text-left">GL Account</th>
                                <th className="border px-4 py-2 text-center">Status</th>
                                <th className="border px-4 py-2 text-left text-sm">Created By</th>
                                <th className="border px-4 py-2 text-left text-sm">Created Date</th>
                                <th className="border px-4 py-2 text-left text-sm">Edited By</th>
                                <th className="border px-4 py-2 text-left text-sm">Edited Date</th>
                                <th className="border px-4 py-2 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mappings.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="border px-4 py-6 text-center text-sm text-gray-500">
                                        No mappings found.
                                    </td>
                                </tr>
                            ) : mappings.map((mapping) => (
                                <tr key={`${mapping.analysis_code}-${mapping.gl_account_code}`} className="hover:bg-gray-50">
                                    <td className="border px-4 py-2">
                                        <Typography className="font-mono font-bold">{mapping.analysis_code}</Typography>
                                        <Typography className="text-xs text-gray-500">{mapping.analysis_description || '—'}</Typography>
                                    </td>
                                    <td className="border px-4 py-2">
                                        <Typography className="font-mono font-bold">{mapping.gl_account_code}</Typography>
                                        <Typography className="text-xs text-gray-500">{mapping.gl_account_name || '—'}</Typography>
                                    </td>
                                    <td className="border px-4 py-2">
                                        <div className="flex justify-center items-center">
                                            {mapping.is_active ? <FaCheck className="text-green-700" /> : <FaTimes className="text-red-700" />}
                                        </div>
                                    </td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{mapping.input_by || '—'}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{formatTimestamp(mapping.input_date)}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{mapping.edit_by || '—'}</td>
                                    <td className="border px-4 py-2 text-sm text-gray-600">{formatTimestamp(mapping.edit_date)}</td>
                                    <td className="border px-4 py-2 text-center">
                                        <Button
                                            onClick={() => setDeleteTarget(mapping)}
                                            className="bg-transparent shadow-none"
                                            title="Deactivate mapping"
                                        >
                                            <FaTrash className="text-red-700" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                title="Deactivate Mapping"
                description={`Are you sure you want to deactivate ${deleteTarget?.analysis_code || 'this mapping'} for ${deleteTarget?.gl_account_code || 'this GL account'}?`}
                confirmText="Deactivate"
                cancelText="Cancel"
                variant="danger"
                loading={deleteLoading}
                error={deleteError}
                onCancel={() => {
                    if (deleteLoading) return;
                    setDeleteTarget(null);
                    setDeleteError('');
                }}
                onConfirm={confirmDeleteMapping}
            />
        </div>
    );
};

const OwnerAnalysisCodes = () => {
    const [activeTab, setActiveTab] = useState('analysis');

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">GL Master Data</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4" />
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Manage GL analysis and controlled sub-analysis codes from one place.
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <Card className="w-full border-t">
                    <CardBody className="space-y-6">
                        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
                            <button
                                type="button"
                                onClick={() => setActiveTab('analysis')}
                                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === 'analysis'
                                        ? 'border-[#FF5733] text-[#FF5733]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                GL Analysis Codes
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('subanalysis')}
                                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === 'subanalysis'
                                        ? 'border-[#FF5733] text-[#FF5733]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                GL Sub-analysis Codes
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('mappings')}
                                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === 'mappings'
                                        ? 'border-[#FF5733] text-[#FF5733]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                GL Analysis Mappings
                            </button>
                        </div>

                        {activeTab === 'analysis' && (
                            <MasterCodeSection
                                title="GL Analysis Codes"
                                description="Master analysis codes used by GL postings, expense mappings, and legacy owner workflows."
                                endpoint="/api/owner-analysis-codes"
                                categoryOptions={analysisCategoryOptions}
                                createLabel="Add New GL Analysis Code"
                                deactivateLabel="Deactivate GL Analysis Code"
                                emptyMessage="No GL analysis codes found."
                            />
                        )}
                        {activeTab === 'subanalysis' && (
                            <MasterCodeSection
                                title="GL Sub-analysis Codes"
                                description="Controlled secondary dimensions used to classify GL sub-analysis values."
                                endpoint="/api/system/gl-sub-analysis-codes"
                                categoryOptions={subAnalysisCategoryOptions}
                                createLabel="Add New GL Sub-analysis Code"
                                deactivateLabel="Deactivate GL Sub-analysis Code"
                                emptyMessage="No GL sub-analysis codes found."
                            />
                        )}
                        {activeTab === 'mappings' && <MappingSection />}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default OwnerAnalysisCodes;
